import type { AddToInventorySpecimenItemDto } from '@/api/ApiClient';
import { MAX_ADD_TO_INVENTORY_QUANTITY } from '@/lib/addToInventoryBatch';

/**
 * Per-row UI state for the "Customize each" add-to-inventory mode. Each row becomes its own inventory sample.
 * Mirrors the substrate fields of the "Same details for all" form plus an optional per-row image.
 */
export interface SpecimenRowState {
  /** Stable client-side key for React lists and add/remove (not sent to the server). */
  id: number;
  substrateName: string;
  /** Single substrate material per row (kept as an array to match the request DTO shape). */
  materialIds: number[];
  vendor: string;
  lot: string;
  orientation: string;
  holder: string;
  thickness: string;
  quality: string;
  size: string;
  /** Optional per-row image; sent only for rows that have one. */
  file: File | null;
}

/** Maximum specimen rows in customize mode — same cap as identical-batch Quantity. */
export const MAX_VARIED_SPECIMENS = MAX_ADD_TO_INVENTORY_QUANTITY;

/** Creates a blank specimen row with the given stable id. */
export function createEmptySpecimenRow(id: number): SpecimenRowState {
  return {
    id,
    substrateName: '',
    materialIds: [],
    vendor: '',
    lot: '',
    orientation: '',
    holder: '',
    thickness: '',
    quality: '',
    size: '',
    file: null,
  };
}

/** Returns the next stable row id (max existing + 1, or 1 when empty). */
export function nextSpecimenRowId(rows: SpecimenRowState[]): number {
  return rows.length ? Math.max(...rows.map((r) => r.id)) + 1 : 1;
}

/** Trims a string field to undefined when empty so we don't send blank strings the server would store. */
function emptyToUndefined(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
}

/**
 * Maps the customize-mode rows into the request's `specimens[]`. Image bytes are not included here — those go
 * through the multipart endpoint's parallel file/index collections (see {@link collectSpecimenImages}).
 */
export function buildVariedSpecimens(rows: SpecimenRowState[]): AddToInventorySpecimenItemDto[] {
  return rows.map((row) => ({
    label: undefined,
    substrateName: emptyToUndefined(row.substrateName),
    substrateVendor: emptyToUndefined(row.vendor),
    substrateLot: emptyToUndefined(row.lot),
    substrateOrientation: emptyToUndefined(row.orientation),
    sampleHolder: emptyToUndefined(row.holder),
    thickness: emptyToUndefined(row.thickness),
    quality: emptyToUndefined(row.quality),
    size: emptyToUndefined(row.size),
    substrateMaterialIds: row.materialIds,
  }));
}

/**
 * Collects the per-row images into the two parallel collections the multipart endpoint expects:
 * `files[k]` is the image for the specimen at `indexes[k]`. Rows without an image contribute nothing.
 */
export function collectSpecimenImages(rows: SpecimenRowState[]): { files: File[]; indexes: number[] } {
  const files: File[] = [];
  const indexes: number[] = [];
  rows.forEach((row, index) => {
    if (row.file) {
      files.push(row.file);
      indexes.push(index);
    }
  });
  return { files, indexes };
}

/** True when at least one row carries an image (decides JSON vs multipart endpoint). */
export function hasAnySpecimenImage(rows: SpecimenRowState[]): boolean {
  return rows.some((row) => row.file != null);
}

/**
 * Client-side validation for customize mode; returns an error message or null when valid.
 * Mirrors the server rules: 1..MAX rows, and every row needs at least one substrate material.
 */
export function validateVariedRows(rows: SpecimenRowState[]): string | null {
  if (rows.length < 1) {
    return 'Add at least one specimen.';
  }
  if (rows.length > MAX_VARIED_SPECIMENS) {
    return `You can register up to ${MAX_VARIED_SPECIMENS} specimens at a time.`;
  }
  if (rows.some((row) => row.materialIds.length === 0)) {
    return 'Each specimen must have a material selected.';
  }
  return null;
}
